import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Prop, Watch } from "@stencil/core";

export interface RadioGroupChangeEventDetail {
    /* eslint-disable-next-line */
    value: any;
}

/**
 * @slot (default) - The radio items.
 * @slot label - The label.
 */
@Component({
    tag: "uic-radio-group",
    styleUrl: "radio-group.scss",
    shadow: true
})
export class RadioGroup implements ComponentInterface {
    @Element()
    private host: HTMLUicRadioGroupElement;

    /**
     * The text to show next to the radio buttons.
     */
    @Prop() readonly label?: string;

    /**
     * The value of the radio button that should be checked.
     */
    /* eslint-disable-next-line */
    @Prop({ mutable: true }) value: any;

    /**
     * Emitted when the value changes.
     */
    @Event() uicChange: EventEmitter<RadioGroupChangeEventDetail>;

    @Watch("value")
    valueChanged(value) {
        this.setupRadios();
        this.uicChange.emit({ value });
    }

    componentDidLoad() {
        this.setupRadios();
    }

    private handleKeydown(e: KeyboardEvent) {
        const key = e.key;
        if (["ArrowLeft", "ArrowUp"].includes(key)) {
            this.moveFocus(-1);
            e.preventDefault();
        } else if (["ArrowRight", "ArrowDown"].includes(key)) {
            this.moveFocus(1);
            e.preventDefault();
        }
    }

    private moveFocus(direction: number) {
        const radios = this.getRadios().filter((radio) => !radio.disabled);
        const radioIndex = radios.findIndex((radio) => radio.value === this.value);

        let nextRadioIndex = radioIndex + direction;
        nextRadioIndex = (nextRadioIndex + radios.length) % radios.length;

        const nextRadio = radios[nextRadioIndex];
        nextRadio.focus();

        this.value = nextRadio.value;
    }

    private setupRadios() {
        const radios = this.getRadios();

        const first = radios.find((radio) => !radio.disabled);
        const checked = radios.find((radio) => radio.value === this.value && !radio.disabled);

        const focusable = checked || first;
        if (!focusable) {
            return;
        }

        for (const radio of radios) {
            const tabindex = radio === focusable ? 0 : -1;
            radio.tabIndex = tabindex;
            radio.checked = radio === checked;
        }
    }

    private getRadios() {
        return Array.from(this.host.querySelectorAll("uic-radio"));
    }

    private handleChange(e: Event) {
        const target = e.target as HTMLUicRadioElement;

        if (!target || target.nodeName !== "UIC-RADIO") {
            return;
        }

        if (target.checked) {
            this.value = target.value;
        }

        e.stopPropagation();
    }

    render() {
        return (
            <Host role='radiogroup' onKeydown={(e) => this.handleKeydown(e)} onUicChange={(e) => this.handleChange(e)}>
                <div class='uic-radio-group__label'>
                    <slot name='label'>{this.label}</slot>
                </div>
                <slot></slot>
            </Host>
        );
    }
}
